import React, { useState, useEffect, useMemo } from 'react';
// import { useNavigate } from 'react-router-dom';
import ResultsList from './components/ResultsList';
import { normalize } from './utils/normalize';
import { dbService } from './services/db';
import { archiveService } from '../../services/archiveService';
import { invygoIdsFor, invygoHistoryFor } from '../../services/invygoIds';
import { USE_CONTRACTS_V2, v2RecordsByIds } from '../../services/contractsV2';
import { withEjarOpenClose } from './utils/analysis';
import logger from '../../utils/logger';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';

import Layout from '../../components/Layout';
import Icon from '../../ui/Icon';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import Dialog from '../../ui/Dialog/Dialog';
import EmptyState from '../../ui/EmptyState';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import { Input } from '../../ui/Field/Field';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { ICONS } from '../../ui/icon-map';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useModal } from '../../contexts/ModalContext';

// 'Agreement' (the old Invygo sheet) and 'Contract No.' (E-Check) must meet on one key.
const contractKey = (v) => (v === null || v === undefined ? '' : String(v).trim().toUpperCase());

/**
 * Multi-Car Analysis reads the system history only: the E-Checks Yelo keeps from Ejar. The CSV upload and the
 * "Invygo ID" file upload are gone (owner, 28 Sep 2026: no manual lists); the Invygo booking id of each contract
 * comes from invygoIds (Ejar's booking -> broker reference; the old sheet's history for old contracts).
 */

export default function MultiCarAnalysis({ embedded = false }) {
    // const _navigate = useNavigate();
    const { currentUser } = useAuth();
    const [results, setResults] = useState([]);
    const [uploadSummary, setUploadSummary] = useState(null);
    const [bookingIdMap, setBookingIdMap] = useState(new Map());
    const [bookingIdError, setBookingIdError] = useState(null);
    const [search, setSearch] = useState("");
    const [currentSearchInput, setCurrentSearchInput] = useState("");
    const [_selectedContract, _setSelectedContract] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');
    const { showToast, showAlert } = useModal();

    // Worker setup
    const workerRef = React.useRef(null);
    // where the E-Checks read start: Ejar's pick-up of a contract opened before it never names the first car read
    const sinceRef = React.useRef(null);

    useEffect(() => {
        // Instantiate worker on mount with module type for imports
        workerRef.current = new Worker(new URL("./workers/multiCarWorker.js", import.meta.url), { type: 'module' });

        workerRef.current.onerror = (err) => {
            console.error('[DEBUG Worker Error]', err); // eslint-disable-line no-console
            showToast('Worker Error: ' + err.message, 'error');
        };

        workerRef.current.onmessageerror = (err) => {
            console.error('[DEBUG Worker Message Error]', err); // eslint-disable-line no-console
        };

        workerRef.current.onmessage = (event) => {
            const { success, results, summary, error, progress } = event.data;

            if (progress) {
                setProcessingStatus(progress);
                return;
            }

            if (success) {
                setUploadSummary(summary);
                // each contract opened and closed by the owner's rule (6 Oct 2026): Ejar's pick-up / drop-off when
                // they come before the E-Checks - the store's records of the multi-car contracts only. Not read
                // (the flag off, or the engine refused): the E-Check times, as before - said in the log only
                const list = results || [];
                const nos = [...new Set(list.map((r) => contractKey(r.contractNo)).filter(Boolean))];
                setProcessingStatus(`Reading Ejar's open and close of ${nos.length} contract(s)...`);
                (USE_CONTRACTS_V2 && nos.length
                    // at most a minute: the E-Check times rather than a page that never stops "uploading" (review)
                    ? Promise.race([v2RecordsByIds(nos, 'all'), new Promise((_r, reject) => setTimeout(() => reject(new Error('timed out')), 60000))])
                    : Promise.resolve([]))
                    .then((records) => withEjarOpenClose(list, records, sinceRef.current))
                    .catch((e) => { logger.warn('[Multi-Car] Ejar open / close not read - E-Check times shown', e); return list; })
                    .then((final) => {
                        setResults(final);
                        // Save to IndexedDB (Async)
                        dbService.save('results', final).catch(err => logger.error("DB Save Error", err));
                        dbService.save('summary', summary).catch(err => logger.error("DB Save Error", err));
                        setUploading(false);
                        setProcessingStatus('');
                    });
            } else if (error) {
                logger.error('Worker error:', error);
                showAlert({ title: 'Processing Error', message: 'Error analyzing the history: ' + error, isError: true });
                setUploading(false);
                setProcessingStatus('');
            }
        };

        // Load saved data from IndexedDB
        const loadSavedData = async () => {
            try {
                const savedResults = await dbService.get('results');
                if (savedResults) setResults(savedResults);

                const savedSummary = await dbService.get('summary');
                if (savedSummary) setUploadSummary(savedSummary);
            } catch (err) {
                logger.error("Failed to load saved data", err);
            }
        };
        loadSavedData();

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
            }
        };
    }, [showAlert, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

    // Invygo's booking id of the multi-car contracts shown - read for those contracts only, whenever the results
    // change (a new analysis, or the results saved in this browser). A failed read is said, never shown as "no id".
    useEffect(() => {
        const nos = [...new Set((results || []).map(r => contractKey(r.contractNo || r.contract)).filter(Boolean))];
        if (!nos.length) { setBookingIdMap(new Map()); setBookingIdError(null); return undefined; }
        let alive = true;
        invygoIdsFor(nos)
            .then((found) => {
                if (!alive) return;
                setBookingIdMap(new Map([...found].map(([no, v]) => [no, v.invygoId])));
                setBookingIdError(null);
            })
            .catch((err) => {
                logger.error('[Multi-Car] Invygo booking ids not loaded', err);
                if (alive) setBookingIdError('Invygo booking IDs could not be loaded - the Booking ID column is empty. Analyze again to retry.');
            });
        return () => { alive = false; };
    }, [results]);

    // --- System Analysis Handler ---
    const handleSystemAnalysis = async () => {
        setUploading(true);
        setResults([]);
        setUploadSummary(null);
        setProcessingStatus('Fetching System History...');

        try {
            // 1. Fetch Full History - the contracts store (USE_CONTRACTS_V2) serves the E-Checks of a period, not the
            //    whole archive: the last 12 months, a week a request
            let historyData;
            if (USE_CONTRACTS_V2) {
                setProcessingStatus('Fetching the E-Checks of the last 12 months...');
                const now = Date.now();
                sinceRef.current = now - 365 * 86400000;
                historyData = await archiveService.loadWindow(now - 365 * 86400000, now, {
                    onProgress: (done, total) => setProcessingStatus(`Fetching the E-Checks of the last 12 months... ${done}/${total}`),
                });
            } else {
                sinceRef.current = null;
                historyData = await archiveService.load();
            }

            if (!historyData || historyData.length === 0) {
                showAlert({ title: "No Data", message: "No history data found in system.", isError: true });
                setUploading(false);
                return;
            }

            // 1.5 Customer names: with the flags off the old Invygo sheet's history names its customers (by agreement);
            //     the store's E-Checks carry each contract's own customer. The booking ids are read for the
            //     multi-car contracts once the worker answers (the effect above). The history only here: the
            //     whole archive's contracts would be dozens of engine calls for names the engine does not give.
            const newCustomerNameMap = new Map();
            if (!USE_CONTRACTS_V2) {
                setProcessingStatus('Fetching Invygo customer names...');
                const contractNos = historyData.map(row => row['Contract No.'] || row['Contract No'] || row['Agreement']);
                const history = await invygoHistoryFor(contractNos).catch(_e => new Map());
                history.forEach((v, no) => { if (v.customerName) newCustomerNameMap.set(no, v.customerName); });
            }

            setProcessingStatus(`Analyzing ${historyData.length} records...`);

            // 2. Map to Worker Format (raw E-Check row shape expected by the shared segmentation)
            const mappedData = historyData.map(row => {
                const ag = contractKey(row['Contract No.'] || row['Contract No'] || row['Agreement']);
                return {
                    'Contract No.': ag,
                    'Plate Number': row['Plate Number'] || row['Plate No.'] || row['Plate No'],
                    'Customer Name': newCustomerNameMap.get(ag) || row['Customer'] || row['Customer Name'] || row['Name'],
                    'Car Model': row['Model'] || row['Car Model'] || row['Vehicle'],
                    'Check Type': row['Check Type'],
                    'Transaction Date': row['Transaction Date'] || row['Date'] || row['Revenue Date'],
                    'Branch': row['Branch'] || row['Pick-up Branch'] || ''
                };
            });

            // 3. Send to Worker in Chunks (Prevent message size issues)
            if (workerRef.current) {
                workerRef.current.postMessage({ type: 'RESET' });

                const chunkSize = 10000;
                for (let i = 0; i < mappedData.length; i += chunkSize) {
                    const chunk = mappedData.slice(i, i + chunkSize);
                    workerRef.current.postMessage({ type: 'CHUNK', data: chunk });
                }

                workerRef.current.postMessage({ type: 'DONE' });
            }

            adminService.logUserAction('ANALYZE_SYSTEM_HISTORY', `Analyzed ${historyData.length} history records`, currentUser);

        } catch (error) {
            logger.error("System Analysis Failed", error);
            showToast("Failed to analyze system history.", 'error');
            setUploading(false);
        }
    };

    // Filter Logic
    const filteredResults = useMemo(() => {
        if (!search.trim()) return results;

        const s = search.trim().toLowerCase();
        const sNorm = normalize(s);

        return results.filter(row => {
            const cNo = row.contractNo || row.contract;
            if (cNo && (cNo.toLowerCase().includes(s) || normalize(cNo).includes(sNorm))) return true;

            const cName = row.customerName || row['Customer Name'];
            if (cName && (cName.toLowerCase().includes(s) || normalize(cName).includes(sNorm))) return true;

            if (row.carsCount && row.carsCount.toString() === s) return true;

            const bookingId = bookingIdMap.get(contractKey(cNo)) || '';
            if (bookingId && (bookingId.toLowerCase().includes(s) || normalize(bookingId).includes(sNorm))) return true;

            // Check cars
            if (row.carDetails) {
                return row.carDetails.some(c => {
                    const plate = String(c.plateNumber || '');
                    return plate.toLowerCase().includes(s) || normalize(plate).includes(sNorm);
                });
            }
            return false;
        });
    }, [search, results, bookingIdMap]);

    const runSearch = () => {
        setSearch(currentSearchInput);
        adminService.logUserAction('SEARCH_MULTICAR', `Searched Multi-Car for "${currentSearchInput}"`, currentUser, { query: currentSearchInput });
    };

    const content = (
            <div className="flex flex-col gap-4">

                {/* Blocking progress while a file / the history is processed */}
                <Dialog
                    open={uploading}
                    title="Processing"
                    size="sm"
                    hideClose
                    closeOnOverlay={false}
                    closeOnEscape={false}
                >
                    <LoadingSpinner message={processingStatus || 'Processing...'} />
                </Dialog>

                {/* Source: the system history only (no file upload - owner, 28 Sep 2026) */}
                <Card>
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            onClick={handleSystemAnalysis}
                            variant="primary"
                            disabled={uploading}
                            leadingIcon={<Icon glyph={ICONS.database} size="sm" />}
                        >
                            Analyze System History
                        </Button>
                        <span className="text-xs text-gray-600">
                            {USE_CONTRACTS_V2 ? 'The E-Checks of the last 12 months, from Ejar' : 'The E-Check archive, from Ejar'}
                        </span>
                    </div>
                    {bookingIdError && <p className="text-xs text-red-600 m-0 mt-2" role="alert">{bookingIdError}</p>}
                </Card>

                {/* Summary Stats */}
                {uploadSummary && (
                    <StatGrid min={180}>
                        <Stat label="Total Rows" value={uploadSummary.totalRows} icon={ICONS.reports} />
                        <Stat label="Total Contracts" value={uploadSummary.totalContracts} icon={ICONS.contracts} />
                        <Stat label="Single Car" value={uploadSummary.singleCarContracts} icon={ICONS.fleet} />
                        <Stat label="Multi-Car" value={uploadSummary.multiCarContracts} icon={ICONS.multi_car} tone="warning" />
                    </StatGrid>
                )}

                <Card padding="none">
                    {/* Search Bar */}
                    {(uploadSummary || results.length > 0) && (
                        <div className="ds-toolbar p-3 border-b border-gray-200">
                            <div className="flex-1 min-w-[220px] max-w-md">
                                <Input
                                    type="search"
                                    aria-label="Search multi-car results"
                                    prefix={<Icon glyph={ICONS.search} size="sm" />}
                                    value={currentSearchInput}
                                    onChange={e => setCurrentSearchInput(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') runSearch();
                                    }}
                                    placeholder="Search contract, booking ID, plate..."
                                />
                            </div>
                            <Button onClick={runSearch}>
                                Search
                            </Button>
                            <span className="ds-toolbar__spacer" />
                            <Button
                                variant="ghost"
                                leadingIcon={<Icon glyph={ICONS.refresh} size="sm" />}
                                onClick={() => {
                                    setSearch("");
                                    setCurrentSearchInput("");
                                    setResults([]);
                                    setUploadSummary(null);
                                    dbService.clear().catch(err => logger.error(err));
                                }}
                            >
                                Reset
                            </Button>
                        </div>
                    )}
                    <ResultsList
                        results={filteredResults}
                        bookingIdMap={bookingIdMap}
                        onSelectContract={_setSelectedContract}
                    />
                </Card>

                <Dialog
                    open={!!_selectedContract}
                    onClose={() => _setSelectedContract(null)}
                    title={_selectedContract ? `Contract ${_selectedContract.contractNo || _selectedContract.contract}` : ''}
                    description={_selectedContract ? (_selectedContract.customerName || _selectedContract['Customer Name'] || undefined) : undefined}
                    size="lg"
                    footer={<Button onClick={() => _setSelectedContract(null)}>Close</Button>}
                >
                    {_selectedContract && (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>Plate</TableHeader>
                                        <TableHeader>From</TableHeader>
                                        <TableHeader>To</TableHeader>
                                        <TableHeader>Source</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(_selectedContract.carDetails || []).map((car, i) => (
                                        <TableRow key={i}>
                                            <TableCell className="font-mono">{car.plateNumber}</TableCell>
                                            <TableCell>{car.from}</TableCell>
                                            <TableCell>{car.to}</TableCell>
                                            <TableCell>{car.branch}</TableCell>
                                        </TableRow>
                                    ))}
                                    {!(_selectedContract.carDetails || []).length && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <EmptyState icon={ICONS.car} title="No car segments found." />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Dialog>
            </div>
    );
    // a tab of History Lens (embedded) or a page of its own
    if (embedded) return content;
    return (
        <Layout title="Multi-Car Analysis" subtitle="Contracts that used more than one car" backPath="/report-history">
            {content}
        </Layout>
    );
}
